import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiUpload,
  FiEye, FiEyeOff, FiSearch, FiImage, FiAlertTriangle,
  FiArrowUp, FiArrowDown, FiLayers,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { clearSiteSettingsCache } from '../hooks/useSiteSettings';

/* ── Shared inline styles (matches existing admin panel patterns) ── */
const S = {
  card:      { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px' },
  label:     { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:     { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit' },
  textarea:  { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit', resize: 'vertical', minHeight: 72 },
  btnPrimary:{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' },
  btnGhost:  { background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontWeight: 500, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: '#374151', fontFamily: 'inherit' },
  btnDanger: { background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' },
  iconBtn:   { background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '6px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#374151' },
};

const toSlug = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const EMPTY_FORM = {
  name: '', slug: '', image: '', short_description: '',
  display_order: '', status: 'active', productIds: [],
};

/* ── Upload helper ── */
const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('image', file);
  const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  if (!data.success) throw new Error(data.message || 'Upload failed');
  return data.url;
};

/* ══════════════════════════════════════════════════════════════
   HERO IMAGE CARD
   ══════════════════════════════════════════════════════════════ */
const HeroImageCard = () => {
  const [heroImg,    setHeroImg]    = useState('');
  const [uploading,  setUploading]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    API.get('/site-settings/public/services_hero')
      .then(({ data }) => setHeroImg(data.services_hero?.heroImage || ''))
      .catch(() => {});
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setHeroImg(url);
    } catch { toast.error('Image upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put('/site-settings/services_hero', { heroImage: heroImg });
      clearSiteSettingsCache('services_hero'); // Bust cache so ServicesPage re-fetches the new image
      toast.success('Hero image saved');
    } catch { toast.error('Failed to save hero image'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ ...S.card, marginBottom: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>Services Hero Image</h3>
          <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 3 }}>
            This image appears in the hero section of the public Services page.
          </p>
        </div>
        <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
          <FiCheck size={14} /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Preview */}
        <div style={{ width: 200, height: 120, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {heroImg
            ? <img src={heroImg} alt="Hero" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <FiImage size={32} color="#d1d5db" />
          }
        </div>

        <div style={{ flex: 1, minWidth: 180 }}>
          <label style={S.label}>Image URL</label>
          <input
            style={{ ...S.input, marginBottom: 10 }}
            value={heroImg}
            onChange={(e) => setHeroImg(e.target.value)}
            placeholder="https://..."
          />
          <button style={S.btnGhost} onClick={() => fileRef.current?.click()} disabled={uploading}>
            <FiUpload size={14} /> {uploading ? 'Uploading…' : 'Upload Image'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   DELETE CONFIRM MODAL
   ══════════════════════════════════════════════════════════════ */
const DeleteModal = ({ service, onConfirm, onCancel, deleting }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
    <div style={{ ...S.card, maxWidth: 420, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FiAlertTriangle size={20} color="#dc2626" />
        </div>
        <h3 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>Delete Service</h3>
      </div>
      <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
        Are you sure you want to delete <strong>"{service.name}"</strong>?
      </p>
      {service.products?.length > 0 && (
        <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>
          This service has {service.products.length} linked product{service.products.length !== 1 ? 's' : ''}. 
          The products themselves will <strong>not</strong> be deleted — only the service and its links.
        </p>
      )}
      <p style={{ fontSize: 13, color: '#ef4444', marginBottom: 20 }}>This action cannot be undone.</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
        <button style={S.btnDanger} onClick={onConfirm} disabled={deleting}>
          <FiTrash2 size={14} /> {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   SERVICE FORM (Add / Edit)
   ══════════════════════════════════════════════════════════════ */
const ServiceForm = ({ editService, onDone, onCancel, allProducts }) => {
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [prodSearch,setProdSearch]= useState('');
  const fileRef = useRef();

  /* Populate form when editing */
  useEffect(() => {
    if (editService) {
      setForm({
        name:              editService.name || '',
        slug:              editService.slug || '',
        image:             editService.image || '',
        short_description: editService.short_description || '',
        display_order:     editService.display_order ?? '',
        status:            editService.status || 'active',
        productIds:        (editService.products || []).map((p) => p.id),
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editService]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleNameChange = (e) => {
    const name = e.target.value;
    set('name', name);
    if (!editService) set('slug', toSlug(name));
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      set('image', url);
    } catch { toast.error('Image upload failed'); }
    finally { setUploading(false); }
  };

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      productIds: f.productIds.includes(id)
        ? f.productIds.filter((x) => x !== id)
        : [...f.productIds, id],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Service name is required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        display_order: form.display_order !== '' ? parseInt(form.display_order) : 0,
        productIds: form.productIds,
      };
      if (editService) {
        await API.put(`/public-services/admin/${editService.id}`, payload);
        toast.success('Service updated');
      } else {
        await API.post('/public-services/admin', payload);
        toast.success('Service created');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const filteredProds = allProducts.filter((p) =>
    p.name.toLowerCase().includes(prodSearch.toLowerCase()) ||
    (p.category?.name || '').toLowerCase().includes(prodSearch.toLowerCase())
  );

  return (
    <div style={{ ...S.card, marginBottom: 24, border: '1.5px solid #6366f1' }}>
      {/* Form header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>
          {editService ? 'Edit Service' : 'Add New Service'}
        </h3>
        <button onClick={onCancel} style={{ ...S.iconBtn, color: '#6b7280' }}><FiX size={18} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Name */}
        <div>
          <label style={S.label}>Service Name *</label>
          <input style={S.input} value={form.name} onChange={handleNameChange} placeholder="e.g. Business Printing" />
        </div>

        {/* Slug */}
        <div>
          <label style={S.label}>Slug</label>
          <input
            style={S.input}
            value={form.slug}
            onChange={(e) => set('slug', toSlug(e.target.value))}
            placeholder="auto-generated"
          />
        </div>

        {/* Display Order */}
        <div>
          <label style={S.label}>Display Order</label>
          <input
            style={S.input}
            type="number"
            min="0"
            value={form.display_order}
            onChange={(e) => set('display_order', e.target.value)}
            placeholder="0"
          />
        </div>

        {/* Status */}
        <div>
          <label style={S.label}>Status</label>
          <select
            style={{ ...S.input, cursor: 'pointer' }}
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Short Description */}
      <div style={{ marginBottom: 16 }}>
        <label style={S.label}>Short Description</label>
        <textarea
          style={S.textarea}
          value={form.short_description}
          onChange={(e) => set('short_description', e.target.value)}
          placeholder="Shown on the service card on the public page…"
        />
      </div>

      {/* Image */}
      <div style={{ marginBottom: 20 }}>
        <label style={S.label}>Service Image</label>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Preview */}
          <div style={{ width: 100, height: 70, borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {form.image
              ? <img src={form.image} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
              : <FiImage size={22} color="#d1d5db" />
            }
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <input
              style={{ ...S.input, marginBottom: 8 }}
              value={form.image}
              onChange={(e) => set('image', e.target.value)}
              placeholder="https://... or upload below"
            />
            <button style={S.btnGhost} onClick={() => fileRef.current?.click()} disabled={uploading}>
              <FiUpload size={13} /> {uploading ? 'Uploading…' : 'Upload Image'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageFile} />
          </div>
        </div>
      </div>

      {/* Linked Products */}
      <div style={{ marginBottom: 20 }}>
        <label style={S.label}>
          Linked Products{' '}
          <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            ({form.productIds.length} selected)
          </span>
        </label>

        {/* Product search */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{ ...S.input, paddingLeft: 32 }}
            value={prodSearch}
            onChange={(e) => setProdSearch(e.target.value)}
            placeholder="Search products…"
          />
        </div>

        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          {filteredProds.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              No products found
            </div>
          ) : (
            filteredProds.map((p) => {
              const checked = form.productIds.includes(p.id);
              return (
                <div
                  key={p.id}
                  onClick={() => toggleProduct(p.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    background: checked ? '#f0f0ff' : '#fff',
                    transition: 'background 0.12s',
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? '#6366f1' : '#d1d5db'}`,
                    background: checked ? '#6366f1' : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {checked && <FiCheck size={11} color="#fff" />}
                  </div>
                  {p.mainImage && (
                    <img src={p.mainImage} alt={p.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    {p.category?.name && <div style={{ fontSize: 11, color: '#9ca3af' }}>{p.category.name}</div>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
        <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
          <FiCheck size={14} /> {saving ? 'Saving…' : editService ? 'Update Service' : 'Add Service'}
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════ */
export default function AdminPublicServices() {
  const [services,    setServices]    = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [editService, setEditService] = useState(null);
  const [deleteTarget,setDeleteTarget]= useState(null);
  const [deleting,    setDeleting]    = useState(false);
  const [search,      setSearch]      = useState('');
  const [statusFilter,setStatusFilter]= useState('');

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search)       params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await API.get('/public-services/admin/all', { params });
      setServices(data.services || []);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await API.get('/public-services/admin/catalog-products');
      setAllProducts(data.products || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => { setEditService(null); setShowForm(true); };
  const openEdit = (s) => { setEditService(s); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditService(null); };

  const handleFormDone = () => { closeForm(); fetchServices(); };

  const handleToggleStatus = async (s) => {
    try {
      await API.patch(`/public-services/admin/${s.id}/status`);
      toast.success(`Service ${s.status === 'active' ? 'deactivated' : 'activated'}`);
      fetchServices();
    } catch { toast.error('Failed to update status'); }
  };

  const handleOrderChange = async (s, direction) => {
    const current = parseInt(s.display_order) || 0;
    const next = direction === 'up' ? Math.max(0, current - 1) : current + 1;
    try {
      await API.patch(`/public-services/admin/${s.id}/order`, { display_order: next });
      fetchServices();
    } catch { toast.error('Failed to update order'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await API.delete(`/public-services/admin/${deleteTarget.id}`);
      toast.success('Service deleted');
      setDeleteTarget(null);
      fetchServices();
    } catch { toast.error('Failed to delete service'); }
    finally { setDeleting(false); }
  };

  return (
    <div style={{ padding: '32px 24px', maxWidth: 1100 }}>

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          service={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', margin: 0 }}>Public Services</h2>
          <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>
            Manage the services shown on the customer-facing Services page.
          </p>
        </div>
        {!showForm && (
          <button style={S.btnPrimary} onClick={openAdd}>
            <FiPlus size={15} /> Add Service
          </button>
        )}
      </div>

      {/* Hero image card */}
      <HeroImageCard />

      {/* Add/Edit Form */}
      {showForm && (
        <ServiceForm
          editService={editService}
          onDone={handleFormDone}
          onCancel={closeForm}
          allProducts={allProducts}
        />
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{ ...S.input, paddingLeft: 32 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services…"
          />
        </div>
        <select
          style={{ ...S.input, width: 'auto', cursor: 'pointer' }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Services table */}
      <div style={S.card}>
        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 13, padding: '24px 0', textAlign: 'center' }}>Loading…</div>
        ) : services.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 0', color: '#9ca3af' }}>
            <FiLayers size={36} style={{ opacity: 0.3, marginBottom: 10, display: 'block', margin: '0 auto 12px' }} />
            <p style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>No services found</p>
            <p style={{ fontSize: 13 }}>
              {search || statusFilter ? 'Try adjusting your filters.' : 'Add your first service to get started.'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                  {['Service', 'Image', 'Linked Products', 'Order', 'Status', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>

                    {/* Name + slug */}
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>/services/{s.slug}</div>
                    </td>

                    {/* Image */}
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ width: 56, height: 40, borderRadius: 6, border: '1px solid #f3f4f6', background: '#f9fafb', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {s.image
                          ? <img src={s.image} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          : <FiImage size={16} color="#d1d5db" />
                        }
                      </div>
                    </td>

                    {/* Products */}
                    <td style={{ padding: '12px 10px', color: '#6b7280' }}>
                      {s.products?.length > 0 ? (
                        <span style={{ background: '#f0f0ff', color: '#6366f1', borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                          {s.products.length} product{s.products.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span style={{ color: '#d1d5db', fontSize: 12 }}>None</span>
                      )}
                    </td>

                    {/* Order controls */}
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontWeight: 600, minWidth: 24, textAlign: 'center' }}>{s.display_order}</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <button style={{ ...S.iconBtn, padding: '3px 5px' }} onClick={() => handleOrderChange(s, 'up')} title="Move up">
                            <FiArrowUp size={12} />
                          </button>
                          <button style={{ ...S.iconBtn, padding: '3px 5px' }} onClick={() => handleOrderChange(s, 'down')} title="Move down">
                            <FiArrowDown size={12} />
                          </button>
                        </div>
                      </div>
                    </td>

                    {/* Status badge */}
                    <td style={{ padding: '12px 10px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                        background: s.status === 'active' ? '#dcfce7' : '#f3f4f6',
                        color:      s.status === 'active' ? '#16a34a' : '#6b7280',
                      }}>
                        {s.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button style={S.iconBtn} onClick={() => openEdit(s)} title="Edit">
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          style={S.iconBtn}
                          onClick={() => handleToggleStatus(s)}
                          title={s.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {s.status === 'active'
                            ? <FiEyeOff size={14} color="#dc2626" />
                            : <FiEye size={14} color="#16a34a" />}
                        </button>
                        <button style={S.iconBtn} onClick={() => setDeleteTarget(s)} title="Delete">
                          <FiTrash2 size={14} color="#dc2626" />
                        </button>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile-responsive note (screen-reader only, actual responsiveness via CSS) */}
    </div>
  );
}